import { SDUIOnClickHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import { SDUIImageElementRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Image/SDUIImageElementRenderer";
import { SDUITileInstanceRendererProps } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/types";
import { SDUITypographyRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/SDUITypographyRenderer";
import { IOptimizeImageOptions } from "@faire/web--source/common/images/optimizeImages";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { cn } from "@faire/web--source/tools/cn";
import { IImageFanTile } from "@faire/web-api--source/indigofair/data/IImageFanTile";
import React from "react";

const FRONT_IMAGE_TILE_WIDTH = "56px";
const BACK_IMAGE_TILE_WIDTH = "44px";
const DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH = 250;

export const SDUIImageFanTileRenderer = ({
  tileElement,
  loggingData,
  renderBehaviour,
}: SDUITileInstanceRendererProps) => {
  const { on_click, image_fan_tile } = tileElement;
  if (!image_fan_tile) {
    return null;
  }

  const { primary_text, image_elements, image_fan_tile_style } = image_fan_tile;
  if (
    !image_elements ||
    !image_elements[0] ||
    !image_elements[1] ||
    !primary_text ||
    !image_fan_tile_style
  ) {
    return null;
  }
  const { animation_on_hover } = image_fan_tile_style;
  const hasHighlightBorderAnimation = animation_on_hover?.includes(
    IImageFanTile.ImageFanTileAnimation.HIGHLIGHT_BORDER
  );
  const hasFanOutImagesAnimation = animation_on_hover?.includes(
    IImageFanTile.ImageFanTileAnimation.FAN_OUT_IMAGES
  );

  const frontImageStyles = {
    width: FRONT_IMAGE_TILE_WIDTH,
    height: "auto",
    isCornersRounded: true,
  };

  const backImageStyles = {
    width: BACK_IMAGE_TILE_WIDTH,
    height: "auto",
    isCornersRounded: true,
  };

  const optimizeImageOptionsWidth =
    image_elements[0].image?.width ?? DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH;
  const optimizeImageOptionsHeight =
    image_elements[0].image?.height ?? DEFAULT_IMAGE_TILE_OPTIMIZE_WIDTH;

  const optimizeImageOptions: IOptimizeImageOptions = {
    resizeMethod: "fill",
    width: optimizeImageOptionsWidth,
    height: optimizeImageOptionsHeight,
  };

  return (
    <SDUIOnClickHandler
      onClickActions={on_click}
      loggingData={loggingData}
      logLegacyClickEvent
    >
      <Row
        className={cn(
          "group border-core-border-muted rounded-sm border border-solid p-[16px]",
          hasHighlightBorderAnimation ? "hover:border-core-surface-hover" : null
        )}
      >
        <div
          className={cn(
            "z-3 transition duration-300",
            hasFanOutImagesAnimation ? "group-hover:-rotate-3" : null
          )}
        >
          <SDUIImageElementRenderer
            imageElement={image_elements[0]}
            loggingData={loggingData}
            renderBehaviour={renderBehaviour}
            imageStyles={frontImageStyles}
            optimizeImageOptions={optimizeImageOptions}
          />
        </div>
        <div
          className={cn(
            "z-2 -ml-[28px] rotate-14 transform self-center transition duration-300",
            hasFanOutImagesAnimation
              ? "group-hover:translate-x-[8px] group-hover:translate-y-[1px] group-hover:rotate-22"
              : null
          )}
        >
          <SDUIImageElementRenderer
            imageElement={image_elements[1]}
            loggingData={loggingData}
            renderBehaviour={renderBehaviour}
            imageStyles={backImageStyles}
            optimizeImageOptions={optimizeImageOptions}
          />
        </div>
        <Spacer width="3x" />
        <SDUITypographyRenderer
          textElement={primary_text}
          loggingData={loggingData}
        />
      </Row>
    </SDUIOnClickHandler>
  );
};
